import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import API from '../../utils/api';
import { useConfirm, useAlert } from '../../components/ConfirmModal';

const ILLUS_COLOR = { done: 'success', generating: 'warning', failed: 'danger', none: 'secondary' };

const AdminStories = () => {
  const confirm = useConfirm();
  const alert = useAlert();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stories, setStories] = useState([]);
  const [total, setTotal]     = useState(0);
  const [pages, setPages]     = useState(1);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const search  = searchParams.get('search')  || '';
  const genre   = searchParams.get('genre')   || '';
  const lang    = searchParams.get('language') || '';
  const illus   = searchParams.get('illus')   || '';
  const sort    = searchParams.get('sort')    || 'newest';
  const page    = parseInt(searchParams.get('page') || '1');
  const [searchInput, setSearchInput] = useState(search);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, limit: 20, sort };
    if (search)  params.search = search;
    if (genre)   params.genre  = genre;
    if (lang)    params.language = lang;
    if (illus)   params.illustrationStatus = illus;
    API.get('/admin/stories', { params })
      .then(r => { setStories(r.data.stories); setTotal(r.data.total); setPages(r.data.pages); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [search, genre, lang, illus, sort, page]);

  useEffect(() => { load(); }, [load]);

  const set = (key, val) => {
    const p = Object.fromEntries(searchParams);
    if (val) p[key] = val; else delete p[key];
    if (key !== 'page') p.page = '1';
    setSearchParams(p);
  };

  const deleteStory = async (id) => {
    const ok = await confirm({ title: 'Delete story?', message: 'This story will be permanently deleted and cannot be recovered.', confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    setDeleting(id);
    try {
      await API.delete(`/admin/stories/${id}`);
      if (selected?._id === id) setSelected(null);
      load();
    } catch (e) { console.error(e); }
    finally { setDeleting(null); }
  };

  return (
    <AdminLayout title="Stories">
      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
        <div className="card-body p-3">
          <div className="row g-2 align-items-center">
            <div className="col-md-3">
              <form onSubmit={e => { e.preventDefault(); set('search', searchInput); }} className="d-flex gap-2">
                <input className="form-control form-control-sm" placeholder="Search title…"
                  value={searchInput} onChange={e => setSearchInput(e.target.value)} style={{ borderRadius: '8px' }} />
                <button type="submit" className="btn btn-sm btn-dream rounded-pill px-3">
                  <i className="bi bi-search"></i>
                </button>
              </form>
            </div>
            <div className="col-auto">
              <select className="form-select form-select-sm" style={{ borderRadius: '8px' }}
                value={genre} onChange={e => set('genre', e.target.value)}>
                <option value="">All Genres</option>
                {['adventure','fantasy','mystery','educational','bedtime','funny','nature','science'].map(g => (
                  <option key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</option>
                ))}
              </select>
            </div>
            <div className="col-auto">
              <select className="form-select form-select-sm" style={{ borderRadius: '8px' }}
                value={lang} onChange={e => set('language', e.target.value)}>
                <option value="">All Languages</option>
                {['en','es','fr','hi','gu','de','pt','it','zh','ja','ar'].map(l => (
                  <option key={l} value={l}>{l.toUpperCase()}</option>
                ))}
              </select>
            </div>
            <div className="col-auto">
              <select className="form-select form-select-sm" style={{ borderRadius: '8px' }}
                value={illus} onChange={e => set('illus', e.target.value)}>
                <option value="">All Illustrations</option>
                <option value="done">Done</option>
                <option value="generating">Generating</option>
                <option value="failed">Failed</option>
                <option value="none">None</option>
              </select>
            </div>
            <div className="col-auto">
              <select className="form-select form-select-sm" style={{ borderRadius: '8px' }}
                value={sort} onChange={e => set('sort', e.target.value)}>
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="mostRead">Most read</option>
              </select>
            </div>
            <div className="col-auto ms-auto d-flex align-items-center gap-3">
              <span className="text-muted small">{total.toLocaleString()} stories</span>
              <button
                className="btn btn-sm btn-outline-secondary rounded-pill"
                style={{ fontSize: '12px' }}
                onClick={async () => {
                  try {
                    const r = await API.get('/admin/export/stories', { responseType: 'blob' });
                    const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `stories_${new Date().toISOString().split('T')[0]}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch (e) { await alert({ title: 'Export failed', message: 'Could not export stories CSV. Please try again.', danger: true }); }
                }}>
                <i className="bi bi-download me-1"></i>Export CSV
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3">
        <div className={selected ? 'col-lg-7' : 'col-12'}>
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border" style={{ color: '#6f42c1' }}></div>
            </div>
          ) : (
            <>
              <div className="card border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead style={{ background: 'var(--page-bg)' }}>
                      <tr>
                        <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>STORY</th>
                        <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>USER / CHILD</th>
                        <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>LANG</th>
                        <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>READS</th>
                        <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>ILLUS</th>
                        <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>DATE</th>
                        <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {stories.length === 0 ? (
                        <tr><td colSpan={7} className="text-center text-muted py-5">No stories found</td></tr>
                      ) : stories.map(s => (
                        <tr key={s._id} style={{ verticalAlign: 'middle', cursor: 'pointer', background: selected?._id === s._id ? '#f8f5ff' : undefined }}
                          onClick={() => setSelected(selected?._id === s._id ? null : s)}>
                          <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)', maxWidth: '220px' }}>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-main)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {s.isFavorite && <i className="bi bi-star-fill text-warning me-1" style={{ fontSize: '11px' }}></i>}
                              {s.title}
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-soft)' }}>{s.genre}</div>
                          </td>
                          <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)' }}>
                            {s.parent && (
                              <Link to={`/admin/users/${s.parent._id}`} onClick={e => e.stopPropagation()}
                                style={{ fontSize: '12px', color: '#6f42c1', fontWeight: 600, display: 'block' }}>
                                {s.parent.name}
                              </Link>
                            )}
                            {s.isMultiChild && s.multiChildIds?.length > 0
                              ? <div style={{ fontSize: '12px', color: 'var(--text-soft)' }}>👨‍👩‍👧‍👦 {s.multiChildIds.map(c => c.name).join(', ')}</div>
                              : <div style={{ fontSize: '12px', color: 'var(--text-soft)' }}>{s.child?.name}, {s.child?.age}y</div>
                            }
                          </td>
                          <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)', fontSize: '13px', color: 'var(--text-muted2)' }}>
                            {s.language?.toUpperCase()}
                          </td>
                          <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)', fontSize: '13px', color: 'var(--text-body)' }}>
                            {s.readCount}
                          </td>
                          <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)' }}>
                            <span className={`badge bg-${ILLUS_COLOR[s.illustrationStatus]} bg-opacity-10 text-${ILLUS_COLOR[s.illustrationStatus]}`} style={{ fontSize: '10px' }}>
                              {s.illustrationStatus}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)', fontSize: '12px', color: 'var(--text-muted2)' }}>
                            {new Date(s.createdAt).toLocaleDateString()}
                          </td>
                          <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)' }}>
                            <button
                              disabled={deleting === s._id}
                              onClick={e => { e.stopPropagation(); deleteStory(s._id); }}
                              className="btn btn-sm btn-outline-danger rounded-pill px-2"
                              style={{ fontSize: '11px' }}>
                              {deleting === s._id ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-trash"></i>}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {pages > 1 && (
                <div className="d-flex justify-content-center gap-2 mt-3">
                  <button className="btn btn-sm btn-outline-secondary rounded-pill" disabled={page <= 1}
                    onClick={() => set('page', String(page - 1))}>← Prev</button>
                  <span className="btn btn-sm disabled">{page} / {pages}</span>
                  <button className="btn btn-sm btn-outline-secondary rounded-pill" disabled={page >= pages}
                    onClick={() => set('page', String(page + 1))}>Next →</button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Story preview panel */}
        {selected && (
          <div className="col-lg-5">
            <div className="card border-0 shadow-sm" style={{ borderRadius: '16px', position: 'sticky', top: '72px' }}>
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <h6 className="fw-bold mb-0" style={{ color: 'var(--text-main)', flex: 1, paddingRight: '8px' }}>{selected.title}</h6>
                  <button onClick={() => setSelected(null)} className="btn btn-sm btn-outline-secondary rounded-pill px-2" style={{ fontSize: '12px', flexShrink: 0 }}>
                    <i className="bi bi-x"></i>
                  </button>
                </div>

                {selected.illustrationUrl && (
                  <img src={selected.illustrationUrl} alt={selected.title}
                    className="w-100 rounded-3 mb-3" style={{ maxHeight: '200px', objectFit: 'cover' }} />
                )}

                <div className="d-flex gap-2 flex-wrap mb-3">
                  <span className="badge rounded-pill" style={{ background: 'var(--purple-section)', color: '#6f42c1', fontSize: '11px' }}>{selected.genre}</span>
                  <span className="badge rounded-pill" style={{ background: 'var(--header-bg)', color: 'var(--text-muted2)', fontSize: '11px' }}>{selected.language?.toUpperCase()}</span>
                  {selected.ageGroup && <span className="badge rounded-pill" style={{ background: 'var(--green-section)', color: '#059669', fontSize: '11px' }}>Age {selected.ageGroup}</span>}
                  {selected.readingTime && <span className="badge rounded-pill" style={{ background: '#fffbeb', color: '#d97706', fontSize: '11px' }}>{selected.readingTime} min</span>}
                </div>

                {selected.summary && (
                  <div className="mb-3">
                    <div style={{ fontSize: '11px', color: 'var(--text-muted2)', fontWeight: 600, marginBottom: '4px' }}>SUMMARY</div>
                    <p style={{ fontSize: '13px', color: 'var(--text-body)', lineHeight: 1.6 }}>{selected.summary}</p>
                  </div>
                )}

                {selected.moralLesson && (
                  <div className="p-3 rounded-3 mb-3" style={{ background: 'var(--green-section)' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted2)', fontWeight: 600, marginBottom: '2px' }}>MORAL LESSON</div>
                    <div style={{ fontSize: '13px', color: '#059669' }}>{selected.moralLesson}</div>
                  </div>
                )}

                <div style={{ maxHeight: '280px', overflowY: 'auto', fontSize: '13px', color: 'var(--text-body)', lineHeight: 1.7, whiteSpace: 'pre-wrap', borderTop: '1px solid var(--row-sep)', paddingTop: '12px' }}>
                  {selected.content}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminStories;
